import { redoCodeFormat } from "@redotech/redo-common-model/code";
import { FulfillmentGroupData } from "@redotech/redo-fulfillment-group-common/fulfillment-group";
import { RedoOutboundLabelsRpcClient } from "@redotech/redo-merchant-fulfillment-rpc-client/client";
import {
  FilterGroup,
  filtersAreEqual,
  getFilterBuilder,
} from "@redotech/redo-views-common/advanced-filters/advanced-filter-data";
import {
  OpensearchPaginationCursor,
  PaginationCursor,
  TableSort,
} from "@redotech/redo-views-common/table";
import { filterGroupsAreEqual } from "@redotech/redo-views-common/utils/util";
import { AdvancedTableFilter } from "@redotech/redo-web/advanced-filters/advanced-filter";
import { TableFetcher } from "@redotech/redo-web/table";

export class FulfillmentGroupsFetcher implements TableFetcher<FulfillmentGroupData> {
  rows: FulfillmentGroupData[] = [];
  private paginationCursor: PaginationCursor | null = null;
  private opensearchPaginationCursor: OpensearchPaginationCursor | null = null;
  private advancedFilters: AdvancedTableFilter[] = [];
  private filterGroup: FilterGroup | undefined;
  private sort: TableSort | null | undefined = null;
  private search: string | undefined;
  private pageSize: number | null = null;
  private pageNumber: number | null = null;

  get currentAdvancedFilters(): AdvancedTableFilter[] {
    return this.advancedFilters;
  }

  constructor(
    private readonly client: RedoOutboundLabelsRpcClient,
    private updateItems: (items: FulfillmentGroupData[]) => void,
    private overrideFilters?: AdvancedTableFilter[],
    private readonly merchantTimezone?: string,
  ) {}

  async counts(
    filters: { [key: string]: string },
    search: string | undefined,
    signal?: AbortSignal | undefined,
    appliedAdvancedFilters?: AdvancedTableFilter[],
    appliedFilterGroup?: FilterGroup,
  ): Promise<{ [key: string]: number }> {
    const nonPartialFilters = (
      this.overrideFilters ||
      appliedAdvancedFilters ||
      []
    ).filter((filter) => !getFilterBuilder(filter.data).isPartial(filter.data));

    const result = await this.client.getFulfillmentGroupSummaryCount(
      {
        advancedFiltersData: nonPartialFilters.map((filter) => filter.data),
        filterGroup: appliedFilterGroup,
        search: search && fulfillmentGroupSearchNormalize(search),
        merchantTimezone: this.merchantTimezone,
      },
      { signal },
    );
    return { total: result.total, totalItems: result.totalItems ?? 0 };
  }

  async *data(
    primaryFilter: string,
    filters: Record<string, string>,
    search: string | undefined,
    sort: TableSort | undefined,
    pageSize: number,
    pageNumber: number,
    signal: AbortSignal,
    passThroughValues: any,
    appliedAdvancedFilters?: AdvancedTableFilter[],
    appliedFilterGroup?: FilterGroup,
  ): AsyncIterator<{
    items: FulfillmentGroupData[];
    refresh: () => Promise<FulfillmentGroupData[]>;
  }> {
    const exportFetching = pageSize === undefined && pageNumber === undefined;
    const exportPageSize = 1000;
    let exportPageNumber = 0;
    search &&= fulfillmentGroupSearchNormalize(search);
    for (;;) {
      const nonPartialFilters = (
        this.overrideFilters ||
        appliedAdvancedFilters ||
        []
      ).filter(
        (filter) => !getFilterBuilder(filter.data).isPartial(filter.data),
      );

      if (
        !nonPartialFilters.every((filter) =>
          this.advancedFilters.some((existingFilter) =>
            filtersAreEqual(existingFilter.data, filter.data),
          ),
        ) ||
        nonPartialFilters.length !== this.advancedFilters.length
      ) {
        this.advancedFilters = nonPartialFilters;
        this.paginationCursor = null;
        this.opensearchPaginationCursor = null;
      }

      // Track filter group changes
      if (!filterGroupsAreEqual(appliedFilterGroup, this.filterGroup)) {
        this.filterGroup = appliedFilterGroup;
        this.paginationCursor = null;
        this.opensearchPaginationCursor = null;
      }

      if (
        sort?.direction !== this.sort?.direction ||
        sort?.key !== this.sort?.key
      ) {
        this.sort = sort;
        this.paginationCursor = null;
        this.opensearchPaginationCursor = null;
      }

      if (search !== this.search) {
        this.search = search;
        this.paginationCursor = null;
        this.opensearchPaginationCursor = null;
        this.rows = [];
        this.updateItems([]);
      }

      if (pageSize !== this.pageSize) {
        this.pageSize = pageSize;
        this.paginationCursor = null;
        this.opensearchPaginationCursor = null;
      }

      if (pageNumber !== this.pageNumber) {
        this.pageNumber = pageNumber;
      } else {
        // if pageNumber has not changed we have no need for pagination and will get a new token with the request
        this.paginationCursor = null;
        this.opensearchPaginationCursor = null;
      }

      try {
        const data = await this.client.getFulfillmentGroupSummaries(
          {
            pageNumber: exportFetching ? exportPageNumber : pageNumber,
            pageSize: exportFetching ? exportPageSize : this.pageSize,
            paginationCursor: this.paginationCursor,
            opensearchPaginationCursor: this.opensearchPaginationCursor,
            advancedFiltersData: this.advancedFilters.map(
              (filter) => filter.data,
            ),
            filterGroup: this.filterGroup,
            search: this.search,
            sort: this.sort
              ? { key: this.sort.key, direction: this.sort.direction }
              : null,
            merchantTimezone: this.merchantTimezone,
          },
          { signal },
        );
        if (exportFetching) {
          yield {
            items: data.fulfillmentGroups,
            refresh: async () => {
              return [];
            },
          };
          if (data.fulfillmentGroups.length < exportPageSize) {
            break;
          } else {
            exportPageNumber++;
            continue;
          }
        }
        if (data.paginationCursor) {
          this.paginationCursor = data.paginationCursor;
        }
        if (data.opensearchPaginationCursor) {
          this.opensearchPaginationCursor = data.opensearchPaginationCursor;
        }

        this.rows = data.fulfillmentGroups;
        this.updateItems(data.fulfillmentGroups);
        yield {
          items: data.fulfillmentGroups,
          refresh: async () => {
            this.rows = [];
            this.updateItems([]);
            this.paginationCursor = null;
            this.opensearchPaginationCursor = null;
            return this.rows;
          },
        };
        break;
      } catch (error: any) {
        if (error.code === "ERR_CANCELED") {
          error.message = "Another request is in flight";
        }
        throw error;
      }
    }
  }
}

export function fulfillmentGroupSearchNormalize(search: string) {
  try {
    const code = redoCodeFormat.read(search.trim());
    if (code.type === "FF") {
      return code.id.toString();
    }
  } catch {
    //
  }
  return search;
}
